import React, {useEffect, useRef, useState} from 'react';
import TopBar from './components/TopBar';
import {makeStyles, useTheme} from '@mui/styles';
import NavBar from './components/NavBar';
import SideBar from "./components/SideBar";
import PropTypes from "prop-types";
import Bottom from "./components/Bottom";
import {getNetworkError, responsive} from "../../actions";
import {useDispatch} from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import useSound from 'use-sound';
import {Offline, Online} from "react-detect-offline";
import {useSnackbar} from "notistack";
import useDashboardLayoutVisibility from '../../hooks/useDashboardLayoutVisibility';
import LayoutContentWrapper from './components/LayoutContentWrapper/LayoutContentWrapper';
import useMixpanel from '../../hooks/useMixpanel';

const NAVBAR_WIDTH = 290;
const SIDEBAR_WIDTH = 300;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        width: '100%',
        // backgroundColor: '#FFFFFF',
        '--dashboard-navbar-width': `${NAVBAR_WIDTH}px`,
        '--dashboard-sidebar-width': `${SIDEBAR_WIDTH}px`,
        backgroundColor: '#FFFFFF'
    },
    wrapper: {
        display: 'flex',
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'hidden',
        paddingTop: 64,
        [theme.breakpoints.up('lg')]: {
            paddingLeft: 'var(--dashboard-navbar-width)',
            paddingRight: 'var(--dashboard-sidebar-width)'
        },
        backgroundColor: '#FFFFFF'
    },
    wrapperPlaceOrder: {
        display: 'flex',
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'hidden',
        paddingTop: 64,
        paddingBottom: 0,
        [theme.breakpoints.up('lg')]: {
            paddingLeft: 'var(--dashboard-navbar-width)',
            paddingRight: '0px'
        },
        backgroundColor: '#FFFFFF'
    },

    contentContainer: {
        display: 'flex',
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'hidden'
    },
    content: {
        flex: '1 1 auto',
        height: '100%',
        minWidth: 0,
        overflow: 'auto',
        backgroundColor: '#FFFFFF'
    }
}));
const DashboardLayout = ({children}) => {
    // ********************************** sound file ****************************************//
    const boopSfx = '/static/sound/interface-button.mp3'
    // *************************************************************************************** //
    const theme = useTheme();
    const classes = useStyles();
    const dispatch = useDispatch()
    const [isMobileNavOpen, setMobileNavOpen] = useState(false);
    const rootRef = useRef(null);
    const greater = useMediaQuery(theme.breakpoints.between("lg", "xl"));
    const greaterThanMid = useMediaQuery(theme.breakpoints.between("md", "lg"));
    const smallToMid = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const lessThanSmall = useMediaQuery(theme.breakpoints.down("sm"));
    const small = useMediaQuery(theme.breakpoints.only("xs"));
    const [play] = useSound(boopSfx);
    const {enqueueSnackbar} = useSnackbar();
    const {isPlaceOrderFlow, showRightSideBar, showBottom} = useDashboardLayoutVisibility({
        hideRightSideBarRoutes: ['/dashboard', '/payments', '/payments/analytics', '/payments/activity', '/payments/partner/:id', '/payments/analytics/credit-utilisation', '/payments/analytics/fuel-purchase', '/payments/analytics/invoice-ageing', '/', '/fuelReconsilation', '/fuelReconsilation/:id', '/fuelReconsilation/:id/:tab', '/fuelReconsilation/:id/:tab/:subTab', '/fuelReconsilation/:id/:tab/:subTab/:subSubTab'],
    });
    const { track } = useMixpanel();


    useEffect(() => {
        dispatch(responsive(theme, greater, greaterThanMid, smallToMid, lessThanSmall, small));
    })

    useEffect(() => {
        function onlineHandler() {
            enqueueSnackbar("Welcome back to online", {
                variant: 'success', anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                }
            })
            dispatch(getNetworkError(false))
        }

        function offlineHandler() {
            enqueueSnackbar("You are offline now", {
                variant: 'error', anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                }
            })
            dispatch(getNetworkError(true))
        }

        window.addEventListener("online", onlineHandler);
        window.addEventListener("offline", offlineHandler);


        return () => {
            window.removeEventListener("online", onlineHandler);
            window.removeEventListener("offline", offlineHandler);
        };
        // eslint-disable-next-line
    }, []);

    // if (blockedIPs.includes(ipAddress)) {
    //     return <h1>Access denied. Your IP address is blocked.</h1>
    // }else

    const handleDelegatedTracking = (e) => {
        try {
            const el = e.target.closest('[data-track-event]');
            if (!el) return;
            const eventName = el.dataset.trackEvent;
            const props = el.dataset.trackProps ? JSON.parse(el.dataset.trackProps) : {};
            track(eventName, props);
        } catch (err) {
            console.warn('Delegated tracking error:', err);
        }
    };

    const handleDelegatedChangeTracking = (e) => {
        try {
            const el = e.target.closest('[data-track-change-event]');
            if (!el) return;

            const eventName = el.dataset.trackChangeEvent;
            const props = el.dataset.trackChangeProps
                ? JSON.parse(el.dataset.trackChangeProps)
                : {};
            const valueProperty = el.dataset.trackValueProperty;
            const valueMap = el.dataset.trackValueMap
                ? JSON.parse(el.dataset.trackValueMap)
                : {};
            const rawValue = e.target.value;

            if (valueProperty) {
                props[valueProperty] = valueMap[rawValue] ?? rawValue;
            }
            track(eventName, props);
        } catch (err) {
            console.warn('Delegated change tracking error:', err);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleDelegatedTracking);
        document.addEventListener('change', handleDelegatedChangeTracking);

        return () => {
            document.removeEventListener('click', handleDelegatedTracking);
            document.removeEventListener('change', handleDelegatedChangeTracking);
        };
    });

    useEffect(() => {
        const root = rootRef.current;
        if (!root || typeof IntersectionObserver === 'undefined') return undefined;

        const trackedImpressions = new WeakMap();
        const impressionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                const element = entry.target;
                const eventName = element.dataset.trackImpressionEvent;
                if (!eventName) return;

                const propsString = element.dataset.trackImpressionProps || '{}';
                const signature = `${eventName}:${propsString}`;
                const elementSignatures = trackedImpressions.get(element) || new Set();
                if (elementSignatures.has(signature)) return;

                try {
                    track(eventName, JSON.parse(propsString));
                    elementSignatures.add(signature);
                    trackedImpressions.set(element, elementSignatures);
                } catch (err) {
                    console.warn('Delegated impression tracking error:', err);
                }
            });
        }, {threshold: 0.25});

        const observeImpressions = (node) => {
            if (!(node instanceof Element)) return;
            if (node.matches('[data-track-impression-event]')) {
                impressionObserver.observe(node);
            }
            node.querySelectorAll?.('[data-track-impression-event]').forEach((element) => {
                impressionObserver.observe(element);
            });
        };

        observeImpressions(root);
        const mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach(observeImpressions);
            });
        });
        mutationObserver.observe(root, {childList: true, subtree: true});

        return () => {
            mutationObserver.disconnect();
            impressionObserver.disconnect();
        };
    }, [track]);

    return (
        <div
            ref={rootRef}
            className={classes.root}
            style={{'--dashboard-sidebar-width': showRightSideBar ? `${SIDEBAR_WIDTH}px` : '0px'}}
        >
            <TopBar onMobileNavOpen={() => setMobileNavOpen(true)} Online={Online} Offline={Offline}/>
            <NavBar
                onMobileClose={() => setMobileNavOpen(false)}
                isPlaceOrderFlow={isPlaceOrderFlow}
                openMobile={isMobileNavOpen}
                play={play}
            />
            <LayoutContentWrapper
                isPlaceOrderFlow={isPlaceOrderFlow}
                wrapperClassName={classes.wrapper}
                wrapperPlaceOrderClassName={classes.wrapperPlaceOrder}
                contentClassName={classes.content}
            >
                {children}
            </LayoutContentWrapper>
            {showRightSideBar && (
                <div>
                    <SideBar
                        onMobileClose={() => setMobileNavOpen(false)}
                        openMobile={isMobileNavOpen}
                    />
                </div>
            )}
            {showBottom && (
                <Bottom onMobileClose={() => setMobileNavOpen(true)} play={play}
                        floatingActionIconSrc="/static/images/navBarMobile/add-newnewOrde.png"/>
            )}
        </div>
    );
};

DashboardLayout.propTypes = {
    children: PropTypes.node
};

export default DashboardLayout;
